import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Routes,
  Route,
  Link,
  useNavigate,
  useSearchParams,
  useParams,
} from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { projectsAPI, tasksAPI, usersAPI, tagsAPI, type Tag } from '../../services/api';
import { TagMultiSelect } from '../shared/TagMultiSelect';
import { TaskDetailPage } from '../tasks';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import {
  PlusIcon,
  SearchIcon,
  FilterIcon,
  EyeIcon,
  EditIcon,
  TrashIcon,
  ArrowLeftIcon,
  TaskIcon,
  CalendarIcon,
  ClockIcon,
  UserIcon,
  FolderIcon,
  ChevronDownIcon,
} from '../icons';

// Premium line icons (1.5px stroke weight)
const Icons = {
  plus: <PlusIcon className="" width={18} height={18} strokeWidth={1.5} />,
  search: <SearchIcon className="" width={18} height={18} strokeWidth={1.5} />,
  filter: <FilterIcon className="" width={16} height={16} strokeWidth={1.5} />,
  eye: <EyeIcon className="" width={16} height={16} strokeWidth={1.5} />,
  edit: <EditIcon className="" width={16} height={16} strokeWidth={1.5} />,
  trash: <TrashIcon className="" width={16} height={16} strokeWidth={1.5} />,
  arrowLeft: <ArrowLeftIcon className="" width={18} height={18} strokeWidth={1.5} />,
  task: <TaskIcon className="" width={20} height={20} strokeWidth={1.5} />,
  calendar: <CalendarIcon className="" width={16} height={16} strokeWidth={1.5} />,
  clock: <ClockIcon className="" width={16} height={16} strokeWidth={1.5} />,
  user: <UserIcon className="" width={16} height={16} strokeWidth={1.5} />,
  folder: <FolderIcon className="" width={16} height={16} strokeWidth={1.5} />,
  chevronDown: <ChevronDownIcon className="" width={16} height={16} strokeWidth={1.5} />,
};

interface Task {
  id: number;
  title: string;
  description?: string;
  status: string;
  priority: string;
  due_date?: string;
  estimated_hours?: number;
  actual_hours?: number;
  project?: {
    id: number;
    name: string;
  };
  assigned_to?: {
    id: number;
    name: string;
  };
  created_by_user: {
    id: number;
    full_name: string;
  };
  created_at: string;
  updated_at: string;
  tags?: Tag[];
  tag_ids?: number[];
}

interface Project {
  id: number;
  name: string;
}

interface User {
  id: number;
  full_name: string;
  email: string;
}

const TaskList: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  const isDark = theme === 'dark';
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<number[]>([]);

  // Horizontal drag scroll
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!tableContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - tableContainerRef.current.offsetLeft);
    setScrollLeft(tableContainerRef.current.scrollLeft);
  }, []);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !tableContainerRef.current) return;
      e.preventDefault();
      const x = e.pageX - tableContainerRef.current.offsetLeft;
      const walk = (x - startX) * 1.5;
      tableContainerRef.current.scrollLeft = scrollLeft - walk;
    },
    [isDragging, startX, scrollLeft],
  );

  const handleMouseLeave = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Theme-aware classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const selectClass = isDark
    ? 'px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const tableHeaderClass = isDark
    ? 'bg-slate-900/80 text-slate-400'
    : 'bg-slate-50 text-slate-500';

  const tableRowClass = isDark
    ? 'border-slate-700/50 hover:bg-slate-700/30'
    : 'border-slate-100 hover:bg-slate-50';

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await tasksAPI.getAll();
      setTasks(response.data.tasks || []);
    } catch (error) {
      console.error('Failed to fetch tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = await confirm({
      title: t('tasks.deleteTitle'),
      message: t('tasks.deleteConfirm'),
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await tasksAPI.delete(id);
      setTasks(tasks.filter((t) => t.id !== id));
    } catch (error) {
      console.error('Failed to delete task:', error);
      showToast(t('tasks.deleteFailed'), 'error');
    }
  };

  const handleSelectTask = (taskId: number, checked: boolean) => {
    setSelectedTasks(checked
      ? [...selectedTasks, taskId]
      : selectedTasks.filter(id => id !== taskId)
    );
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedTasks(checked ? tasks.map(t => t.id!) : []);
  };

  const handleBulkDelete = async () => {
    const confirmed = await confirm({
      title: t('tasks.deleteTitle'),
      message: `Delete ${selectedTasks.length} tasks?`,
      confirmLabel: t('common.delete'),
      cancelLabel: t('common.cancel'),
      variant: 'danger',
    });
    if (!confirmed) return;

    try {
      await tasksAPI.bulkDelete(selectedTasks);
      setTasks(tasks.filter(t => !selectedTasks.includes(t.id!)));
      setSelectedTasks([]);
      showToast(`${selectedTasks.length} tasks deleted`, 'success');
    } catch (error) {
      console.error('Failed to bulk delete tasks:', error);
      showToast(t('tasks.deleteFailed'), 'error');
    }
  };

  const filteredTasks = tasks.filter((task) => {
    const matchesSearch =
      task.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (task.description &&
        task.description.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = !statusFilter || task.status === statusFilter;
    const matchesPriority = !priorityFilter || task.priority === priorityFilter;

    return matchesSearch && matchesStatus && matchesPriority;
  });

  const getStatusColor = (status: string) => {
    if (isDark) {
      switch (status) {
        case 'todo':
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
        case 'in_progress':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case 'completed':
          return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
        case 'blocked':
          return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    } else {
      switch (status) {
        case 'todo':
          return 'bg-slate-100 text-slate-700 border border-slate-200';
        case 'in_progress':
          return 'bg-amber-50 text-amber-700 border border-amber-200';
        case 'completed':
          return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
        case 'blocked':
          return 'bg-rose-50 text-rose-700 border border-rose-200';
        default:
          return 'bg-slate-100 text-slate-700 border border-slate-200';
      }
    }
  };

  const getPriorityColor = (priority: string) => {
    if (isDark) {
      switch (priority) {
        case 'low':
          return 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30';
        case 'medium':
          return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
        case 'high':
          return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
        case 'critical':
          return 'bg-rose-500/20 text-rose-400 border border-rose-500/30';
        default:
          return 'bg-slate-500/20 text-slate-400 border border-slate-500/30';
      }
    } else {
      switch (priority) {
        case 'low':
          return 'bg-cyan-50 text-cyan-700 border border-cyan-200';
        case 'medium':
          return 'bg-amber-50 text-amber-700 border border-amber-200';
        case 'high':
          return 'bg-orange-50 text-orange-700 border border-orange-200';
        case 'critical':
          return 'bg-rose-50 text-rose-700 border border-rose-200';
        default:
          return 'bg-slate-100 text-slate-700 border border-slate-200';
      }
    }
  };

  if (loading) {
    return (
      <div
        className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
      >
        <div>
          <div className="shimmer h-10 w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="shimmer h-16"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6 sm:mb-8">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <div
              className={`hidden sm:flex p-2 rounded-xl ${isDark ? 'bg-violet-500/20' : 'bg-violet-100'}`}
            >
              <span className={isDark ? 'text-violet-400' : 'text-violet-600'}>
                {Icons.task}
              </span>
            </div>
            <h1
              className={`text-xl sm:text-3xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('tasks.title')}
            </h1>
          </div>
          <p
            className={`text-sm sm:text-base ${isDark ? 'text-slate-400' : 'text-slate-600'} sm:ml-14`}
          >
            {t('tasks.totalCount', { count: filteredTasks.length })}
          </p>
        </div>
        <Link
          to="/manager/tasks/new"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white text-sm font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200"
        >
          {Icons.plus}
          {t('tasks.newTask')}
        </Link>
      </div>

      {/* Filters */}
      <div className={`${cardClass} rounded-2xl p-4 mb-6`}>
        {/* Search always visible */}
        <div className="relative">
          <span
            className={`absolute left-4 top-1/2 -translate-y-1/2 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
          >
            {Icons.search}
          </span>
          <input
            type="text"
            placeholder={t('tasks.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${inputClass} pl-11`}
          />
        </div>

        {/* Mobile filter toggle */}
        <button
          onClick={() => setFiltersOpen(!filtersOpen)}
          className={`md:hidden w-full flex items-center justify-between mt-3 py-2 ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
        >
          <span className="flex items-center gap-2 text-sm font-medium">
            {Icons.filter}
            {t('common.filters')}
            {(statusFilter || priorityFilter) && (
              <span className="px-1.5 py-0.5 text-xs rounded-full bg-violet-500 text-white">
                {[statusFilter, priorityFilter].filter(Boolean).length}
              </span>
            )}
          </span>
          <span
            className={`transition-transform duration-200 ${filtersOpen ? 'rotate-180' : ''}`}
          >
            {Icons.chevronDown}
          </span>
        </button>

        {/* Collapsible filters - visible on desktop, toggleable on mobile */}
        <div
          className={`${filtersOpen ? 'block' : 'hidden'} md:flex md:flex-wrap gap-4 mt-3 md:mt-4`}
        >
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className={`w-full md:w-auto mb-3 md:mb-0 ${selectClass}`}
          >
            <option value="">{t('tasks.allStatus')}</option>
            <option value="todo">{t('tasks.status.todo')}</option>
            <option value="in_progress">{t('tasks.status.inProgress')}</option>
            <option value="completed">{t('tasks.status.completed')}</option>
            <option value="blocked">{t('tasks.status.blocked')}</option>
          </select>
          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
            className={`w-full md:w-auto ${selectClass}`}
          >
            <option value="">{t('tasks.allPriority')}</option>
            <option value="low">{t('tasks.priority.low')}</option>
            <option value="medium">{t('tasks.priority.medium')}</option>
            <option value="high">{t('tasks.priority.high')}</option>
            <option value="critical">{t('tasks.priority.critical')}</option>
          </select>
        </div>
      </div>

      {/* Tasks - Empty State */}
      {filteredTasks.length === 0 ? (
        <div className={`${cardClass} rounded-2xl overflow-hidden`}>
          <div
            className={`p-12 text-center ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
          >
            <div
              className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${isDark ? 'bg-slate-900' : 'bg-slate-100'} flex items-center justify-center`}
            >
              <span className={isDark ? 'text-slate-600' : 'text-slate-400'}>
                {Icons.task}
              </span>
            </div>
            <p className="font-medium">{t('tasks.noTasks')}</p>
          </div>
        </div>
      ) : (
        <>
          {/* Mobile Card Layout */}
          <div className="md:hidden space-y-3">
            {filteredTasks.map((task) => (
              <div key={task.id} className={`${cardClass} rounded-2xl p-4`}>
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex-1 min-w-0">
                    <h3
                      className={`font-medium truncate ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      {task.title}
                    </h3>
                    {task.description && (
                      <p
                        className={`text-sm truncate mt-1 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {task.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Link
                      to={`/manager/tasks/${task.id}`}
                      className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-cyan-400' : 'hover:bg-slate-100 text-cyan-600'}`}
                    >
                      {Icons.eye}
                    </Link>
                    <Link
                      to={`/manager/tasks/${task.id}/edit`}
                      className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                    >
                      {Icons.edit}
                    </Link>
                    <button
                      onClick={() => handleDelete(task.id)}
                      className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                    >
                      {Icons.trash}
                    </button>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${getStatusColor(task.status)}`}
                  >
                    {task.status.replace('_', ' ')}
                  </span>
                  <span
                    className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-lg ${getPriorityColor(task.priority)}`}
                  >
                    {task.priority}
                  </span>
                </div>

                <div
                  className={`flex flex-wrap gap-x-4 gap-y-1 text-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                >
                  <span className="flex items-center gap-1">
                    {Icons.folder}
                    <span className="truncate max-w-[120px]">
                      {task.project?.name || t('tasks.noProject')}
                    </span>
                  </span>
                  <span className="flex items-center gap-1">
                    {Icons.user}
                    {task.assigned_to?.name || t('tasks.unassigned')}
                  </span>
                  {task.due_date && (
                    <span className="flex items-center gap-1">
                      {Icons.calendar}
                      {new Date(task.due_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Desktop Table Layout */}
          <div
            className={`hidden md:block ${cardClass} rounded-2xl overflow-hidden`}
          >
            <div
              ref={tableContainerRef}
              className={`overflow-auto max-h-[calc(100vh-450px)] cursor-grab select-none ${isDragging ? 'cursor-grabbing' : ''}`}
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              {selectedTasks.length > 0 && (
                <div className={`mb-4 p-4 rounded-xl flex items-center justify-between ${isDark ? 'bg-violet-900/30 border border-violet-700/50' : 'bg-violet-50 border border-violet-200'}`}>
                  <span className={`text-sm font-medium ${isDark ? 'text-violet-300' : 'text-violet-700'}`}>
                    {selectedTasks.length} {selectedTasks.length === 1 ? 'task selected' : 'tasks selected'}
                  </span>
                  <button
                    onClick={handleBulkDelete}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
              <table className="min-w-full relative">
                <thead className={`sticky top-0 z-10 ${tableHeaderClass}`}>
                  <tr>
                    <th className="px-4 py-4 w-12">
                      <input
                        type="checkbox"
                        checked={selectedTasks.length > 0 && selectedTasks.length === filteredTasks.length}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                      />
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('tasks.columns.task')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('tasks.columns.project')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('tasks.columns.assignee')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('tasks.columns.status')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('tasks.columns.priority')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('tasks.columns.dueDate')}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('common.tags') || 'Tags'}
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-semibold uppercase tracking-wider">
                      {t('common.actions')}
                    </th>
                  </tr>
                </thead>
                <tbody
                  className={
                    isDark
                      ? 'divide-y divide-slate-700/50'
                      : 'divide-y divide-slate-100'
                  }
                >
                  {filteredTasks.map((task) => (
                    <tr key={task.id} className={tableRowClass}>
                      <td className="px-4 py-4 w-12">
                        <input
                          type="checkbox"
                          checked={selectedTasks.includes(task.id!)}
                          onChange={(e) => handleSelectTask(task.id!, e.target.checked)}
                          className="w-4 h-4 cursor-pointer"
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <div
                            className={`text-sm font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                          >
                            {task.title}
                          </div>
                          {task.description && (
                            <div
                              className={`text-sm truncate max-w-xs ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                            >
                              {task.description}
                            </div>
                          )}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={
                              isDark ? 'text-slate-500' : 'text-slate-400'
                            }
                          >
                            {Icons.folder}
                          </span>
                          {task.project?.name || t('tasks.noProject')}
                        </div>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={
                              isDark ? 'text-slate-500' : 'text-slate-400'
                            }
                          >
                            {Icons.user}
                          </span>
                          {task.assigned_to?.name || t('tasks.unassigned')}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${getStatusColor(task.status)}`}
                        >
                          {task.status.replace('_', ' ')}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-lg ${getPriorityColor(task.priority)}`}
                        >
                          {task.priority}
                        </span>
                      </td>
                      <td
                        className={`px-6 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={
                              isDark ? 'text-slate-500' : 'text-slate-400'
                            }
                          >
                            {Icons.calendar}
                          </span>
                          {task.due_date
                            ? new Date(task.due_date).toLocaleDateString()
                            : '-'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {task.tags && task.tags.slice(0, 3).map((tag: any) => (
                            <span
                              key={tag.id}
                              style={{
                                backgroundColor: `#${tag.color}26`,
                                color: `#${tag.color}`,
                                borderColor: `#${tag.color}4d`,
                              }}
                              className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full border"
                            >
                              {tag.name}
                            </span>
                          ))}
                          {task.tags && task.tags.length > 3 && (
                            <span className={`text-xs font-medium px-2 py-0.5 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
                              +{task.tags.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Link
                            to={`/manager/tasks/${task.id}`}
                            className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-slate-700 text-cyan-400' : 'hover:bg-slate-100 text-cyan-600'}`}
                            title={t('common.view')}
                          >
                            {Icons.eye}
                          </Link>
                          <Link
                            to={`/manager/tasks/${task.id}/edit`}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title={t('common.edit')}
                          >
                            {Icons.edit}
                          </Link>
                          <button
                            onClick={() => handleDelete(task.id)}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                            title={t('common.delete')}
                          >
                            {Icons.trash}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const TaskForm: React.FC<{ taskId?: string }> = ({ taskId }) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const isDark = theme === 'dark';
  const [searchParams] = useSearchParams();
  const projectIdFromUrl = searchParams.get('project_id');
  const columnIdFromUrl = searchParams.get('column_id');
  const isFromBoard = !!projectIdFromUrl;

  const [task, setTask] = useState<any>({
    title: '',
    description: '',
    status: 'todo',
    priority: 'medium',
    item_type: 'task',
    estimated_hours: 0,
    project: { id: 0, name: '' },
    tag_ids: [],
  });
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [components, setComponents] = useState<any[]>([]);
  const [versions, setVersions] = useState<any[]>([]);
  const [orgTags, setOrgTags] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const isEdit = !!taskId;

  // Theme-aware classes
  const cardClass = isDark
    ? 'bg-slate-900/50 border border-slate-700/50 backdrop-blur-sm'
    : 'bg-white border border-slate-200 shadow-sm';

  const inputClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white placeholder-slate-500 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 placeholder-slate-400 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const selectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors';

  const labelClass = isDark
    ? 'block text-sm font-medium text-slate-300 mb-2'
    : 'block text-sm font-medium text-slate-700 mb-2';

  const multiSelectClass = isDark
    ? 'w-full px-4 py-2.5 bg-slate-900/50 border border-slate-700 text-white rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors h-32'
    : 'w-full px-4 py-2.5 bg-white border border-slate-200 text-slate-900 rounded-xl focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-colors h-32';

  useEffect(() => {
    fetchData();
    if (taskId) {
      fetchTask();
    }
  }, [taskId]);

  // Auto-fill project when coming from board
  useEffect(() => {
    if (projectIdFromUrl && projects.length > 0 && !isEdit) {
      const projectId = Number(projectIdFromUrl);
      const selectedProject = projects.find((p) => p.id === projectId);
      if (selectedProject) {
        setTask((prev: any) => ({
          ...prev,
          project: { id: selectedProject.id, name: selectedProject.name },
        }));
        fetchProjectData(projectId);
      }
    }
  }, [projectIdFromUrl, projects, isEdit]);

  // Fetch components and versions when project changes
  useEffect(() => {
    if (task.project?.id && task.project.id > 0) {
      fetchProjectData(task.project.id);
    }
  }, [task.project?.id]);

  const fetchData = async () => {
    try {
      const [projectsResponse, usersResponse, tagsResponse] = await Promise.all([
        projectsAPI.getAll(),
        usersAPI.getAll(),
        tagsAPI.getAll(),
      ]);
      setProjects(projectsResponse.data.projects || []);
      setUsers(usersResponse.data.users || []);
      setOrgTags(tagsResponse.data || []);
    } catch (error) {
      console.error('Failed to fetch form data:', error);
    }
  };

  const fetchProjectData = async (projectId: number) => {
    try {
      const [componentsRes, versionsRes] = await Promise.all([
        fetch(`/api/v1/projects/${projectId}/components`)
          .then((r) => r.json())
          .catch(() => ({ components: [] })),
        fetch(`/api/v1/projects/${projectId}/versions`)
          .then((r) => r.json())
          .catch(() => ({ versions: [] })),
      ]);
      setComponents(componentsRes.components || []);
      setVersions(versionsRes.versions || []);
    } catch (error) {
      console.error('Failed to fetch project data:', error);
    }
  };

  const fetchTask = async () => {
    try {
      const response = await tasksAPI.getById(Number(taskId));
      setTask(response.data.task);
    } catch (error) {
      console.error('Failed to fetch task:', error);
      setError(t('tasks.loadFailed'));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const taskData: any = {
        title: task.title || '',
        description: task.description || '',
        project_id: task.project?.id || 0,
        status: task.status || 'todo',
        priority: task.priority || 'medium',
        item_type: task.item_type || 'task',
        assigned_to_user_id:
          task.assigned_to_user_id || task.assigned_to?.id || null,
        due_date: task.due_date || null,
        start_date: task.start_date || null,
        story_points: task.story_points || null,
        original_estimate: task.original_estimate || null,
        remaining_estimate: task.remaining_estimate || null,
        environment: task.environment || null,
        resolution: task.resolution || null,
        component_ids: task.component_ids || [],
        fix_version_ids: task.fix_version_ids || [],
        affects_version_ids: task.affects_version_ids || [],
        tag_ids: task.tag_ids || [],
      };

      if (isEdit) {
        await tasksAPI.update(Number(taskId), taskData);
        navigate(-1);
      } else {
        // Include board_column_id when creating from board
        if (columnIdFromUrl) {
          taskData.board_column_id = Number(columnIdFromUrl);
        }

        await tasksAPI.create(taskData);

        // Navigate back to board if coming from board, otherwise to task list
        if (isFromBoard && projectIdFromUrl) {
          navigate(`/manager/boards/${projectIdFromUrl}`);
        } else {
          navigate('/manager/tasks');
        }
      }
    } catch (error: any) {
      setError(error.response?.data?.error || t('tasks.saveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { value: 'todo', label: t('tasks.status.todo') },
    { value: 'in_progress', label: t('tasks.status.inProgress') },
    { value: 'completed', label: t('tasks.status.completed') },
    { value: 'blocked', label: t('tasks.status.blocked') },
  ];

  const priorityOptions = [
    { value: 'low', label: t('tasks.priority.low') },
    { value: 'medium', label: t('tasks.priority.medium') },
    { value: 'high', label: t('tasks.priority.high') },
    { value: 'critical', label: t('tasks.priority.critical') },
  ];

  const itemTypeOptions = [
    { value: 'task', label: t('kanban.task') },
    { value: 'story', label: t('kanban.story') },
    { value: 'bug', label: t('kanban.bug') },
    { value: 'subtask', label: t('kanban.subtask') },
  ];

  const resolutionOptions = [
    { value: '', label: t('tasks.resolutions.unresolved') },
    { value: 'fixed', label: t('tasks.resolutions.fixed') },
    { value: 'wont_fix', label: t('tasks.resolutions.wontFix') },
    { value: 'duplicate', label: t('tasks.resolutions.duplicate') },
    {
      value: 'cannot_reproduce',
      label: t('tasks.resolutions.cannotReproduce'),
    },
    { value: 'incomplete', label: t('tasks.resolutions.incomplete') },
    { value: 'done', label: t('tasks.resolutions.done') },
  ];

  return (
    <div
      className={`min-h-screen p-6 ${isDark ? 'bg-slate-900' : 'bg-slate-50'}`}
    >
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className={`p-2 rounded-xl transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            {Icons.arrowLeft}
          </button>
          <div>
            <h1
              className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {isEdit ? t('tasks.editTask') : t('tasks.createTask')}
            </h1>
          </div>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-rose-500/10 border border-rose-500/20 text-rose-400 rounded-xl">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Basic Info Section */}
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('tasks.details')}
            </h2>

            <div className="space-y-5">
              <div>
                <label className={labelClass}>{t('tasks.form.title')} *</label>
                <input
                  type="text"
                  required
                  value={task.title || ''}
                  onChange={(e) => setTask({ ...task, title: e.target.value })}
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  {t('tasks.form.description')}
                </label>
                <textarea
                  rows={4}
                  value={task.description || ''}
                  onChange={(e) =>
                    setTask({ ...task, description: e.target.value })
                  }
                  className={inputClass}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className={labelClass}>
                    {t('tasks.form.project')} *
                  </label>
                  {isFromBoard ? (
                    <div
                      className={`${isDark ? 'bg-slate-900/50 border-slate-700 text-slate-400' : 'bg-slate-100 border-slate-200 text-slate-600'} px-4 py-2.5 border rounded-xl`}
                    >
                      {task.project?.name || t('common.loading')}
                    </div>
                  ) : (
                    <select
                      required
                      value={task.project?.id || ''}
                      onChange={(e) => {
                        const projectId = Number(e.target.value);
                        const selectedProject = projects.find(
                          (p) => p.id === projectId,
                        );
                        setTask({
                          ...task,
                          project: selectedProject
                            ? {
                                id: selectedProject.id,
                                name: selectedProject.name,
                              }
                            : undefined,
                        });
                      }}
                      className={selectClass}
                    >
                      <option value="">{t('tasks.form.selectProject')}</option>
                      {projects.map((project) => (
                        <option key={project.id} value={project.id}>
                          {project.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>

                <div>
                  <label className={labelClass}>{t('kanban.task')} Type</label>
                  <select
                    value={task.item_type || 'task'}
                    onChange={(e) =>
                      setTask({ ...task, item_type: e.target.value })
                    }
                    className={selectClass}
                  >
                    {itemTypeOptions.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {/* Status & Priority Section */}
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('common.status')} & {t('tasks.columns.priority')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelClass}>{t('common.status')}</label>
                <select
                  value={task.status || 'todo'}
                  onChange={(e) => setTask({ ...task, status: e.target.value })}
                  className={selectClass}
                >
                  {statusOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>
                  {t('tasks.columns.priority')}
                </label>
                <select
                  value={task.priority || 'medium'}
                  onChange={(e) =>
                    setTask({ ...task, priority: e.target.value })
                  }
                  className={selectClass}
                >
                  {priorityOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>{t('tasks.resolution')}</label>
                <select
                  value={task.resolution || ''}
                  onChange={(e) =>
                    setTask({ ...task, resolution: e.target.value || null })
                  }
                  className={selectClass}
                >
                  {resolutionOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Assignment Section */}
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('tasks.assignee')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('tasks.assignee')}</label>
                <select
                  value={task.assigned_to_user_id || task.assigned_to?.id || ''}
                  onChange={(e) =>
                    setTask({
                      ...task,
                      assigned_to_user_id: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  className={selectClass}
                >
                  <option value="">{t('tasks.unassigned')}</option>
                  {users.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.full_name || user.email}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>{t('kanban.storyPoints')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.5"
                  value={task.story_points || ''}
                  onChange={(e) =>
                    setTask({
                      ...task,
                      story_points: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Dates Section */}
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('common.date')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <div>
                <label className={labelClass}>{t('tasks.startDate')}</label>
                <input
                  type="date"
                  value={task.start_date || ''}
                  onChange={(e) =>
                    setTask({ ...task, start_date: e.target.value || null })
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>
                  {t('tasks.columns.dueDate')}
                </label>
                <input
                  type="date"
                  value={task.due_date || ''}
                  onChange={(e) =>
                    setTask({ ...task, due_date: e.target.value || null })
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Time Tracking Section */}
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('tasks.timeTracking')}
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div>
                <label className={labelClass}>
                  {t('tasks.originalEstimate')} (h)
                </label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={task.original_estimate || ''}
                  onChange={(e) =>
                    setTask({
                      ...task,
                      original_estimate: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>{t('tasks.timeSpent')} (h)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={task.time_spent || ''}
                  onChange={(e) =>
                    setTask({
                      ...task,
                      time_spent: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  className={inputClass}
                />
              </div>

              <div>
                <label className={labelClass}>{t('tasks.remaining')} (h)</label>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={task.remaining_estimate || ''}
                  onChange={(e) =>
                    setTask({
                      ...task,
                      remaining_estimate: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  className={inputClass}
                />
              </div>
            </div>
          </div>

          {/* Environment Section */}
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('tasks.environment')}
            </h2>

            <textarea
              rows={2}
              placeholder={t('tasks.environmentPlaceholder')}
              value={task.environment || ''}
              onChange={(e) =>
                setTask({ ...task, environment: e.target.value || null })
              }
              className={inputClass}
            />
          </div>

          {/* Components & Versions Section */}
          {task.project?.id > 0 && (
            <div className={`${cardClass} rounded-2xl p-6`}>
              <h2
                className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
              >
                {t('tasks.components')} & {t('tasks.versions')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                {components.length > 0 && (
                  <div>
                    <label className={labelClass}>
                      {t('tasks.components')}
                    </label>
                    <select
                      multiple
                      value={task.component_ids || []}
                      onChange={(e) => {
                        const selected = Array.from(
                          e.target.selectedOptions,
                          (option) => Number(option.value),
                        );
                        setTask({ ...task, component_ids: selected });
                      }}
                      className={multiSelectClass}
                    >
                      {components.map((comp) => (
                        <option key={comp.id} value={comp.id}>
                          {comp.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {versions.length > 0 && (
                  <>
                    <div>
                      <label className={labelClass}>
                        {t('tasks.fixVersions')}
                      </label>
                      <select
                        multiple
                        value={task.fix_version_ids || []}
                        onChange={(e) => {
                          const selected = Array.from(
                            e.target.selectedOptions,
                            (option) => Number(option.value),
                          );
                          setTask({ ...task, fix_version_ids: selected });
                        }}
                        className={multiSelectClass}
                      >
                        {versions
                          .filter((v) => v.status !== 'archived')
                          .map((ver) => (
                            <option key={ver.id} value={ver.id}>
                              {ver.name}
                            </option>
                          ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>
                        {t('tasks.affectsVersions')}
                      </label>
                      <select
                        multiple
                        value={task.affects_version_ids || []}
                        onChange={(e) => {
                          const selected = Array.from(
                            e.target.selectedOptions,
                            (option) => Number(option.value),
                          );
                          setTask({ ...task, affects_version_ids: selected });
                        }}
                        className={multiSelectClass}
                      >
                        {versions.map((ver) => (
                          <option key={ver.id} value={ver.id}>
                            {ver.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}

                {components.length === 0 && versions.length === 0 && (
                  <p
                    className={`${isDark ? 'text-slate-400' : 'text-slate-500'} text-sm col-span-3`}
                  >
                    No components or versions configured for this project.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Tags Section */}
          <div className={`${cardClass} rounded-2xl p-6`}>
            <h2
              className={`text-lg font-semibold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}
            >
              {t('common.tags') || 'Tags'}
            </h2>
            <TagMultiSelect
              selectedTagIds={task.tag_ids || []}
              onChange={(ids) => setTask({ ...task, tag_ids: ids })}
              orgTags={orgTags}
              isManager={true}
              onTagCreated={(tag) => {
                tagsAPI.create({ name: tag.name, color: tag.color }).then((res) => {
                  const newTag = res.data;
                  setOrgTags([...orgTags, newTag]);
                  setTask({ ...task, tag_ids: [...(task.tag_ids || []), newTag.id] });
                }).catch(() => showToast('Failed to create tag', 'error'));
              }}
              onTagDeleted={(id) => {
                tagsAPI.delete(id).then(() => {
                  setOrgTags(orgTags.filter(t => t.id !== id));
                  setTask({ ...task, tag_ids: task.tag_ids?.filter((tid: number) => tid !== id) || [] });
                }).catch(() => showToast('Failed to delete tag', 'error'));
              }}
            />
          </div>

          {/* Form Actions */}
          <div className="flex flex-col-reverse md:flex-row justify-end gap-3 pt-6">
            <button
              type="button"
              onClick={() => {
                if (isFromBoard && projectIdFromUrl) {
                  navigate(`/manager/boards/${projectIdFromUrl}`);
                } else {
                  navigate(-1);
                }
              }}
              className={`px-5 py-2.5 font-medium rounded-xl transition-colors ${
                isDark
                  ? 'bg-slate-700 hover:bg-slate-600 text-white'
                  : 'bg-slate-100 hover:bg-slate-200 text-slate-700'
              }`}
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2.5 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold rounded-xl shadow-lg shadow-violet-500/25 transition-all duration-200 disabled:opacity-50"
            >
              {loading
                ? t('common.saving')
                : isEdit
                  ? t('tasks.updateTask')
                  : t('tasks.createTask')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const TaskDetailPageWrapper: React.FC = () => {
  return <TaskDetailPage />;
};

const TaskFormWrapper: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <TaskForm taskId={id} />;
};

const TaskManagement: React.FC = () => {
  return (
    <Routes>
      <Route index element={<TaskList />} />
      <Route path="new" element={<TaskForm />} />
      <Route path=":id" element={<TaskDetailPageWrapper />} />
      <Route path=":id/edit" element={<TaskFormWrapper />} />
    </Routes>
  );
};

export default TaskManagement;
